import { FabricImage, FabricText, Group, Line, Path, Rect } from "fabric";
import calcAreaUtils from "./calcAreaUtils";
import { theme } from "../../react/components/GlobalStyle";
import { Vector3D } from "../lod2";
import intl from "react-intl-universal";
import plotUtils from "./plotUtils";

const imageName = "result image";
const crossName = "gwa cross";
const operatorRatioName = "operator ratio";

const names = Object.freeze({imageName, crossName, operatorRatioName});

const drawCross = (plot, x, y, color, value) => {
    const armLength = 0.03 * Math.min(plot.canvas.getWidth(), plot.canvas.getHeight());
    const thickness = armLength / 10;
    const rect1 = new Rect({
        width: armLength,
        height: thickness,
        fill: color,
        originX: "center",
        originY: "center",
    });

    const rect2 = new Rect({
        width: thickness,
        height: armLength,
        fill: color,
        originX: "center",
        originY: "center",
    });

    const crossGroup = new Group([rect1, rect2], {
        left: x,
        top: y,
        angle: 45,
        originX: "center",
        originY: "center",
        evented: false,
        selectable: false,
    });

    const text = new FabricText(value.toFixed(2), {
        left: x,
        top: y,
        fontSize: armLength,
        color: color,
    });

    const group = new Group([crossGroup, text], {
        evented: false,
        selectable: false,
        nonInteractive: true,
        mxwellname: crossName,
    });

    const rescale = () => {
        const newScale = 1 / plot.canvas.getZoom();
        crossGroup.scaleX = newScale;
        crossGroup.scaleY = newScale;
        group.setCoords();
        text.scaleX = newScale;
        text.scaleY = newScale;
        text.setCoords();
    };
    plot.canvas.on("rescale", rescale);
    rescale();

    return group;
};

const getPointCoordinatesMetersInTopView = (u, v, result, calcArea) => {
    const uDirectionRatio = (u + 0.5) / result.numberU;
    const vDirectionRatio = (v + 0.5) / result.numberV;
    const x = calcArea.originX
            + uDirectionRatio * calcArea.uaxisX
            + vDirectionRatio * calcArea.vaxisX;
    const y = calcArea.originY
            + uDirectionRatio * calcArea.uaxisY
            + vDirectionRatio * calcArea.vaxisY;
    return {x, y};
};

const getPointCoordinatesMetersInOrthogonalView = (u, v, result, calcArea) => {
    const { originX, originY, uaxisX, uaxisY, uaxisZ, vaxisX, vaxisY, vaxisZ } = calcArea;
    const vectorU = new Vector3D(uaxisX, uaxisY, uaxisZ);
    const vectorV = new Vector3D(vaxisX, vaxisY, vaxisZ);
    const lengthU = vectorU.length();
    const lengthV = vectorV.length();
    const x = originX + (u + 0.5) / result.numberU * lengthU;
    const y = originY + (v + 0.5) / result.numberV * lengthV;
    return {x, y};
};

const updateGwaCross = (boundaryClipped, layer, calculation, calcArea, plot, result, resultDetails, getPointCoordinatesMeters) => {
    const calculationId = calculation.id;
    const oldCross = layer.getObjects().find(o => o.calculationId === calculationId && o.mxwellname === crossName);
    if(oldCross) {
        layer.removeObject(oldCross);
    }
    if(resultDetails && resultDetails.gwaU !== undefined && resultDetails.gwaU !== null) {
        const scale = plot.refScale;
        const {gwaU, gwaV, gwa, gwaBoundaryU, gwaBoundaryV, gwaBoundary} = resultDetails;
        const {x, y} = boundaryClipped
            ? getPointCoordinatesMeters(gwaBoundaryU, gwaBoundaryV, result, calcArea)
            : getPointCoordinatesMeters(gwaU, gwaV, result, calcArea);
        const cross = drawCross(plot, x / scale, -y / scale, "black", boundaryClipped ? gwaBoundary : gwa);
        cross.calculationId = calculationId;
        cross.visible = result.shouldDisplay;
        layer.addObject(cross);
    }
};

const onBoundaryClipped = (value, layer, calculation, calcArea, plot, result, resultDetails, topView) => {
    const calculationId = calculation.id;
    const image = layer.getObjects().find(o => o.calculationId === calculationId && o.mxwellname === imageName);
    const {boundary} = calcArea;
    if (image && boundary) {
        if(value === true) {
            const scale = plot.refScale;
            let scaledBoundary;
            if (topView) {
                scaledBoundary = boundary.map(path => path.map(p => ({x: p.x / scale, y: -p.y / scale})));
            } else {
                const { originX, originY, uaxisX, uaxisY, uaxisZ, vaxisX, vaxisY, vaxisZ } = calcArea;
                const lengthU = new Vector3D(uaxisX, uaxisY, uaxisZ).length();
                const lengthV = new Vector3D(vaxisX, vaxisY, vaxisZ).length();
                const det = uaxisX * vaxisY - vaxisX * uaxisY;
                scaledBoundary = boundary.map(path => path.map(p => {
                    const dx = p.x - originX;
                    const dy = p.y - originY;
                    const ratioU = (dx * vaxisY - dy * vaxisX) / det;
                    const ratioV = (dy * uaxisX - dx * uaxisY) / det;
                    const localX = originX + ratioU * lengthU;
                    const localY = originY + ratioV * lengthV;
                    return {x: localX / scale, y: -localY / scale};
                }));
            }
            const pathData = plot.createSvgPath(scaledBoundary);
            const clipShape = new Path(pathData, {
                absolutePositioned: true,
                fillRule: "evenodd",
                fill: "black"
            });
            image.clipPath = clipShape;
        } else {
            image.clipPath = undefined;
        }
    }
    const getPointCoordinatesMeters = topView ? getPointCoordinatesMetersInTopView : getPointCoordinatesMetersInOrthogonalView;
    updateGwaCross(value, layer, calculation, calcArea, plot, result, resultDetails, getPointCoordinatesMeters);
};

const calcPointCanvasCoordinatesTopView = (plot, calcPoint, calcArea) => {
    const scale = plot.refScale;
    const { ratioU, ratioV } = calcPoint;
    const x = (calcArea.originX + ratioU * calcArea.uaxisX + ratioV * calcArea.vaxisX) / scale;
    const y = -(calcArea.originY + ratioU * calcArea.uaxisY + ratioV * calcArea.vaxisY) / scale;
    return [x, y];
};

const calcPointCanvasCoordinatesOrthogonalView = (plot, calcPoint, calcArea) => {
    const scale = plot.refScale;
    const { ratioU, ratioV } = calcPoint;
    const lengthU = new Vector3D(calcArea.uaxisX, calcArea.uaxisY, calcArea.uaxisZ).length();
    const lengthV = new Vector3D(calcArea.vaxisX, calcArea.vaxisY, calcArea.vaxisZ).length();
    const x = (calcArea.originX + ratioU * lengthU) / scale;
    const y = -(calcArea.originY + ratioV * lengthV) / scale;
    return [x, y];
};

const drawOperatorRatioAnalysis = (plot, calcArea, calcPoint, operatorRatioAnalysis, result, calcPointCanvasCoordinates) => {
    const boxWidthToCanvasWidthRatio = 0.26;
    const scale = plot.refScale;
    const entries = Object.entries(operatorRatioAnalysis);

    const lengthU = new Vector3D(calcArea.uaxisX, calcArea.uaxisY, 0).length() / scale;
    const lengthV = new Vector3D(calcArea.vaxisX, calcArea.vaxisY, 0).length() / scale;

    // Target point in canvas coordinates
    const [px, py] = calcPointCanvasCoordinates(plot, calcPoint, calcArea);

    const refSize = Math.min(lengthU, lengthV);
    const fontSize = 12;
    const padding = fontSize * 0.5;
    const rowHeight = fontSize * 1.3;
    const columnGap = fontSize * 0.8;

    const textPairs = entries.map(([key, value]) => ({
        keyText: new FabricText(intl.get("enum.operator." + key), { fontSize, fill: "black" }),
        valueText: new FabricText(`${Math.round(value * 100)}%`, { fontSize, fill: "black" }),
    }));

    let maxKeyWidth = 0;
    let maxValueWidth = 0;
    textPairs.forEach(({ keyText, valueText }) => {
        maxKeyWidth = Math.max(maxKeyWidth, keyText.width);
        maxValueWidth = Math.max(maxValueWidth, valueText.width);
    });

    const tableWidth = padding * 2 + maxKeyWidth + columnGap + maxValueWidth;
    const tableHeight = padding * 2 + entries.length * rowHeight;

    const offset = refSize * 0.08;
    const tableLeft = px + offset;
    const tableTop = py - offset - tableHeight;

    const background = new Rect({
        left: tableLeft,
        top: tableTop,
        width: tableWidth,
        height: tableHeight,
        fill: "white",
        stroke: "black",
        strokeWidth: 8,
        strokeUniform: true,
    });

    textPairs.forEach(({ keyText, valueText }, i) => {
        keyText.set({ left: tableLeft + padding, top: tableTop + padding + i * rowHeight });
        valueText.set({ left: tableLeft + padding + maxKeyWidth + columnGap, top: tableTop + padding + i * rowHeight });
    });

    const allObjects = [
        background,
        ...textPairs.flatMap(({ keyText, valueText }) => [keyText, valueText]),
    ];

    const group = new Group(allObjects, {
        evented: true,
        selectable: true,
        hasControls: false,
        hasBorders: false,
        lockRotation: true,
        hoverCursor: "grab",
        moveCursor: "grabbing",
        mxwellname: operatorRatioName,
        visible: result.visible,
    });

    const line = new Line([0, 0, px, py], {
        stroke: "black",
        strokeWidth: 16,
        strokeUniform: true,
        evented: false,
        selectable: false,
        nonInteractive: true,
        mxwellname: operatorRatioName,
        visible: result.visible,
    });

    const updateLine = () => {
        const center = group.getCenterPoint();
        line.set({ x1: center.x, y1: center.y, x2: px, y2: py });
        line.setCoords();
    };

    const updateResultBoxSettings = group => {
        if(result) {
            result.resultBoxX = group.left;
            result.resultBoxY = group.top;
        }
    };

    group.on("moving", () => {
        updateLine();
        updateResultBoxSettings(group);
    });

    group.on("modified", () => {
        updateResultBoxSettings(group);
    });

    if (result?.resultBoxX !== undefined && result?.resultBoxX !== null && result?.resultBoxY !== undefined && result?.resultBoxY !== null) {
        group.left = result?.resultBoxX;
        group.top = result?.resultBoxY;
        group.setCoords();
    }

    const rescale = () => {
        const targetWidth = boxWidthToCanvasWidthRatio * Math.min(plot.canvas.getWidth(), plot.canvas.getHeight());
        const newScale = targetWidth / group.width / plot.canvas.getZoom();
        const oldHeight = group.height * group.scaleY;
        const bottomLeftX = group.left;
        const bottomLeftY = group.top + oldHeight;
        group.scaleX = newScale;
        group.scaleY = newScale;
        const newHeight = group.height * newScale;
        group.left = bottomLeftX;
        group.top = bottomLeftY - newHeight;
        group.setCoords();
        updateLine();
    };
    plot.canvas.on("rescale", rescale);
    rescale();

    group.operatorLine = line;
    return group;
};

const drawTopViewResult = async (plot, setLegendData, index, layer, calculation, calcArea, resultData, result, slice, fieldMax, pointAnalysisResult, calcPoint) => {
    if (calcAreaUtils.isVertical(calcArea)) { // Wall
        const groupObjects = [];
        const { x, y, width, height, angle } = calcAreaUtils.reverseCalculateArea(calcArea, plot);
        const { angleCounterclockwiseRadians } = calcAreaUtils.getCalcAreaBaseProperties2D(calcArea, plot);
        const rectHeight = height !== 0 ? height : 0.1; // if it is a vertical area
        const fill = height !== 0 ? "transparent" : theme.primaryLight;

        const rect = plot.addRect(layer, 0, 0, width, rectHeight, true, false, {
            originY: "bottom",
            evented: true,
            fill: fill,
            stroke: theme.primaryLight,
            strokeUniform: true,
        });
        groupObjects.push(rect);

        const triangleWidth = width / 8;
        const triangleHeight = -triangleWidth * 0.2;
        const triangleY = 0;
        const triangle = plot.addTriangle(layer, width / 2 - triangleWidth / 2, triangleY, triangleWidth, triangleHeight, true, false, {
            originY: "bottom",
            evented: true,
            fill: fill,
            stroke: theme.primaryLight,
            strokeUniform: true,
        });
        groupObjects.push(triangle);

        const groupCorrection = new Vector3D(0, triangleHeight, 0).rotateAroundZ(angleCounterclockwiseRadians);
        const top = y - groupCorrection.y;
        const left = x + groupCorrection.x;

        const group = new Group(groupObjects, {
            index: index,
            sourceArea: calcArea,
            top: top,
            left: left,
            angle: angle,
            evented: true,
            selectable: true,
            hasControls: false,
            originX: "left",
            originY: "bottom",
            calculationId: calculation.id,
            lockMovementX: true,
            lockMovementY: true,
            visible: result.shouldDisplay,
        });

        layer.addObject(group);

        plot.applyStrokeWithoutTranslation(group, theme.primaryLight, 3);
        if (layer.active) {
            group.set({ visible: result.shouldDisplay });
        }
    } else {
        const image = await FabricImage.fromURL(resultData.image);
        const { left, top, scaleX, scaleY, angle } = calcAreaUtils.calculateSliceImageProperties(calcArea, plot, image);

        const imageOptions = {
            index,
            calculationId: calculation.id,
            left: left,
            top: top,
            angle: angle,
            opacity: 0.4,
            scaleX: scaleX,
            scaleY: scaleY,
            evented: true,
            selectable: true,
            originX: "left",
            originY: "bottom",
            imageSmoothing: false,
            hoverCursor: "crosshair",
            lockMovementX: true,
            lockMovementY: true,
            visible: result.shouldDisplay,
            mxwellname: imageName,
        };

        image.set(imageOptions);
        plot.applyStrokeWithoutTranslation(image, theme.primaryLight, 6);

        layer.addObject(image);
        image.setCoords();

        const {boundaryClipped} = result;
        onBoundaryClipped(boundaryClipped, layer, calculation, calcArea, plot, result, resultData.details, true);

        if (resultData.operatorRatioAnalysis && calcPoint && calcPoint.slice === slice) {
            const ratioGroup = drawOperatorRatioAnalysis(
                plot, 
                calcArea, 
                calcPoint, 
                resultData.operatorRatioAnalysis,
                pointAnalysisResult,
                calcPointCanvasCoordinatesTopView,
            );
            ratioGroup.calculationId = calculation.id;
            ratioGroup.visible = result.shouldDisplay && pointAnalysisResult.visible;
            if (ratioGroup.operatorLine) {
                ratioGroup.operatorLine.calculationId = calculation.id;
                ratioGroup.operatorLine.visible = result.shouldDisplay && pointAnalysisResult.visible;
                layer.addObject(ratioGroup.operatorLine);
            }
            layer.addObject(ratioGroup);
        }

        if (layer.active) {
            if (!calcAreaUtils.isVertical(calcArea)) {
                image.on("mouseout", () => setLegendData());
                image.on("mousemove", opt => setLegendData({ opt, image, calculation, slice, fieldMax }));
            }
            image.set({ visible: result.shouldDisplay });
        }
    }
};

const drawOrthogonalViewResult = async (plot, setLegendData, layer, calculation, calcArea, extraLegendData = {}, resultData, result, slice, pointAnalysisResult, calcPoint) => {
    const { originX, originY } = calcArea;
    const { uaxisX, uaxisY, uaxisZ, vaxisX, vaxisY, vaxisZ } = calcArea;
    const imageBase64 = resultData.image;
    const image = await FabricImage.fromURL(imageBase64);
    const vectorU = new Vector3D(uaxisX, uaxisY, uaxisZ);
    const vectorV = new Vector3D(vaxisX, vaxisY, vaxisZ);
    const lengthU = vectorU.length();
    const lengthV = vectorV.length();
    const left = 0 + (originX / plot.refScale);
    const top = 0 - (originY / plot.refScale);
    const scaleX = lengthU / plot.refScale / image.width;
    const scaleY = lengthV / plot.refScale / image.height;

    image.set({
        calculationId: calculation.id,
        left: left,
        top: top,
        angle: 0,
        opacity: 0.4,
        scaleX: scaleX,
        scaleY: scaleY,
        evented: true,
        originX: "left",
        originY: "bottom",
        imageSmoothing: false,
        hoverCursor: "crosshair",
        stroke: "rgba(255,0,0,1)",
        strokeWidth: 5,
        strokeUniform: true,
        visible: true,
        mxwellname: imageName,
    });
    if (layer.active) {
        image.on("mouseout", () => setLegendData());
        image.on("mousemove", opt => setLegendData({ opt, image, calculation, ...extraLegendData }));
        layer.addObject(image);
        image.setCoords();
    }
    if (result) {
        const {boundaryClipped} = result;
        onBoundaryClipped(boundaryClipped, layer, calculation, calcArea, plot, result, resultData.details, false);
    }
    if (resultData.operatorRatioAnalysis && calcPoint && calcPoint.slice === slice && pointAnalysisResult) {
        const ratioGroup = drawOperatorRatioAnalysis(
            plot, 
            calcArea, 
            calcPoint, 
            resultData.operatorRatioAnalysis,
            {...pointAnalysisResult, resultBoxX: null, resultBoxY: null},
            calcPointCanvasCoordinatesOrthogonalView,
        );
        ratioGroup.calculationId = calculation.id;
        if (ratioGroup.operatorLine) {
            ratioGroup.operatorLine.calculationId = calculation.id;
            layer.addObject(ratioGroup.operatorLine);
        }
        layer.addObject(ratioGroup);
    }
    plot.canvas.renderAll();
    plotUtils.zoomToObject(plot, image, 300);
    await plotUtils.refreshAfterZoom(plot);
};

const fieldArrayResultUtils = {names, drawTopViewResult, onBoundaryClipped, drawOrthogonalViewResult};
export default fieldArrayResultUtils;